import { useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { CaseEndpoint, UpdateEndpointRequest } from '../../../types/case-container'
import { isItSystemType } from './helpers'
import {
  DeviceTypeField,
  DomainField,
  ErrorBanner,
  FormActions,
  HostnameField,
  ImportanceField,
  IpAddressField,
  NotesField,
  OsFieldsRow,
  OuPathField,
  RolesField,
} from './edit-endpoint-modal/edit-endpoint-modal.parts'

interface EditEndpointModalProps {
  endpoint: CaseEndpoint
  onClose: () => void
  onUpdate: (data: UpdateEndpointRequest) => void
  updating: boolean
  error: string | null
  existingCustomRoles: string[]
}

function initialForm(endpoint: CaseEndpoint): UpdateEndpointRequest {
  return {
    hostname: endpoint.hostname,
    deviceType: endpoint.deviceType,
    ipAddresses: endpoint.ipAddresses ?? [],
    operatingSystem: endpoint.operatingSystem ?? '',
    osVersion: endpoint.osVersion ?? '',
    domain: endpoint.domain ?? '',
    ouPath: endpoint.ouPath ?? '',
    importance: endpoint.importance,
    roles: endpoint.roles,
    notes: endpoint.notes ?? '',
  }
}

export function EditEndpointModal(props: EditEndpointModalProps): ReactElement {
  const { endpoint, onClose, onUpdate, updating, error, existingCustomRoles } = props
  const { t } = useTranslation('cases')
  const [formData, setFormData] = useState<UpdateEndpointRequest>(() => initialForm(endpoint))

  const showItSystemFields = isItSystemType(formData.deviceType ?? 'workstation')
  const fieldProps = { formData, setFormData, t }

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault()
    onUpdate(formData)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
          Edit Endpoint
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <ErrorBanner message={error} />}
          <HostnameField {...fieldProps} />
          <DeviceTypeField {...fieldProps} />
          <div className={showItSystemFields ? 'grid grid-cols-2 gap-4' : ''}>
            <IpAddressField {...fieldProps} />
            {showItSystemFields && <DomainField {...fieldProps} />}
          </div>
          {showItSystemFields && <OsFieldsRow {...fieldProps} />}
          {showItSystemFields && <OuPathField {...fieldProps} />}
          <ImportanceField {...fieldProps} />
          {showItSystemFields && (
            <RolesField {...fieldProps} existingCustomRoles={existingCustomRoles} />
          )}
          <NotesField {...fieldProps} />
          <FormActions
            onClose={onClose}
            updating={updating}
            canSubmit={Boolean(formData.hostname)}
          />
        </form>
      </div>
    </div>
  )
}
